import type { CSSProperties } from 'react';
import { ICONS, type IconDef, type IconName } from './icons';

/**
 * The stroke weight every icon is normalised to, expressed in a 24-unit viewBox.
 *
 * 2 at 24 paints 1.33px at a 16px render, which is the weight the app's 24-unit
 * glyphs already had and the same one Feather and Lucide settled on. The app's
 * 16-unit glyphs ran heavier than that and its 24-unit ones lighter, so this
 * lands on the heavier of the two conventions already in the codebase rather
 * than splitting the difference and changing every icon.
 */
const STROKE_AT_24 = 2;

export interface IconProps {
  name: IconName;
  /** Rendered size in px, or any CSS length. Defaults to 1em, so an icon sitting
      inline in a label scales with that label's font size. */
  size?: number | string;
  className?: string;
  style?: CSSProperties;
  /**
   * Accessible name. Omit it for decoration: an icon beside a text label, or
   * inside a button that already has an aria-label, is decorative and gets
   * `aria-hidden` so a screen reader does not announce it twice. Supply it only
   * when the icon is the sole carrier of meaning.
   */
  title?: string;
}

/**
 * One glyph from the set in `icons.tsx`.
 *
 * The one non-obvious thing this does is normalise stroke weight. The icons
 * were authored across four different viewBox sizes (12, 16, 20, 24) with
 * hand-picked stroke widths, and an SVG's stroke scales with its viewBox: a
 * 1.8-weight line in a 16-unit box paints at 2.25px when rendered at 20px,
 * while the same nominal 1.8 in a 24-unit box paints at 1.5px. Rendered side by
 * side, those two icons visibly disagree, which is why the sidebar glyphs read
 * heavier than the settings ones today.
 *
 * So stroke width is derived rather than stored: whatever the viewBox, the
 * painted line lands at the same optical weight. That is also why adding an
 * icon to the set requires no thought about stroke width at all.
 */
export default function Icon({ name, size = '1em', className, style, title }: IconProps) {
  // Widened to IconDef: `as const satisfies` in icons.tsx keeps each entry's
  // literal type, which is what makes IconName exhaustive, but it also means
  // the optional keys are absent from the entries that omit them.
  const def: IconDef = ICONS[name];
  const vbSize = Number(def.viewBox.split(' ')[2]);
  const strokeWidth = ((STROKE_AT_24 * vbSize) / 24) * (def.weight ?? 1);
  const px = typeof size === 'number' ? `${size}px` : size;

  const paint = def.filled
    ? { fill: 'currentColor', stroke: 'none' as const }
    : {
        fill: 'none' as const,
        stroke: 'currentColor',
        strokeWidth,
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
      };

  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: exactly one of <title> and aria-hidden is always present, but the rule cannot see through the conditional
    <svg
      viewBox={def.viewBox}
      width={px}
      height={px}
      className={className}
      style={{ flexShrink: 0, display: 'block', ...style }}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...paint}
    >
      {title ? <title>{title}</title> : null}
      {def.body}
    </svg>
  );
}

export type { IconName };
